import VueRouter from 'vue-router'
import { v4 as uuid } from 'uuid'
import useAttribution from '../src/composables/useAttribution'
import { useHistoryStore, useUserStore } from '@/stores'
import { reportH5Ad } from '@/composables/useH5AdReport'
import { $getDeviceId, $setDeviceId, $setToken } from '@/helpers'
import { isInThirdAppBrowser } from '@/utils/bowserEnv'
import { pageViewTrack, setPageViewParams } from '@/composables/tracks'
import { useAxonPageView } from '@/composables/axon/useAxonPageView'
import { ERROR_PAGE_LEVEL } from '@/constants'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { parseMintegralAdsetTarget } from '@/utils/mintegralAdset'
import routes from '@/routes'
import './ployfills'

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes,
  scrollBehavior(_to, _from, _savedPosition) {
    return { x: 0, y: 0, behavior: 'smooth' }
  },
})

async function getUserInfo(to: any, next: any) {
  const userStore = useUserStore()
  try {
    if (!to.meta?.requiresUserInfo)
      userStore.init()
    else
      await userStore.init()
  }
  catch (error: any) {
    console.error(error)
    next({
      name: 'error',
      query: {
        level: ERROR_PAGE_LEVEL.DEFAULT,
        message: error.msg || error.message,
        redirect: window.encodeURIComponent(window.location.href),
      },
      replace: true,
    })
  }
}

// Router Guards
router.beforeEach(async (to, from, next) => {
  // Set document title
  document.title = to?.meta?.title

  // Mintegral 一个广告链接对应多本书,真实目标在 utm_adset 上,参数优先(不信任路径):
  // {bookId}_{chapterId} 跳章节阅读页;{bookId}_(下划线后为空)跳书详情页;其它形式原样跳转。
  // 仅首次落地导航(from 为初始路由)处理;已在目标路由上时不再跳,防重定向循环
  if (from.name === null && to.query.utm_source === 'mintegral') {
    const target = parseMintegralAdsetTarget(String(to.query.utm_adset ?? ''))
    if (target) {
      const atTarget = to.params.contentId === target.bookId
        && (target.chapterId ? to.params.chapterId === target.chapterId : to.name === 'book')
      if (!atTarget) {
        return next({
          name: target.chapterId ? READER_ROUTE_NAME : 'book',
          params: target.chapterId
            ? { contentId: target.bookId, chapterId: target.chapterId }
            : { contentId: target.bookId },
          query: to.query,
          replace: true,
        })
      }
    }
  }

  // 阅读页路由守卫
  const isReaderRoute = to.name === 'chapter' || to.name === 'chapter-scroll'
  if (isReaderRoute) {
    const isAdTraffic = !!(to.query.utm_source || to.query.bizAdId || to.query.ttclid || to.query.fbclid)

    // 关注点 1：路由模式归一 —— 进入了与品牌阅读器不符的路由（翻页/滚动互窜），切换到本品牌的阅读路由
    const needFixRouteMode = to.name !== READER_ROUTE_NAME
    // 关注点 2：广告流量注入 skipPosition —— 防止"续读上次位置"覆盖广告指定章节；自然流量不注入，保留续读功能
    const needInjectSkipPosition = isAdTraffic && !to.query.skipPosition

    if (needFixRouteMode || needInjectSkipPosition) {
      const query = { ...to.query }
      if (needInjectSkipPosition)
        query.skipPosition = '1'

      return next({
        name: READER_ROUTE_NAME,
        params: to.params,
        query,
        replace: true,
      })
    }
  }

  const userStore = useUserStore()

  // 设置 prevPage, 用于埋点
  const historyStore = useHistoryStore()
  if (to.name !== from.name && from.meta?.title)
    historyStore.$setPrevPage(from.meta.title, window.location.origin, window.location.origin + from.fullPath)
  // 无须登陆
  if (to.meta?.requiresAuth === false) {
    next()
  }
  // 三方绑定
  else if (to.query && to.query.t) {
    $setToken(to.query.t as string)
    const query = { ...to.query }
    delete query.t
    await getUserInfo(to, next)
    void reportH5Ad(to)
    next({
      ...to,
      name: to.name ?? undefined,
      query,
      replace: true,
    })
  }
  /**
   * 内部浏览器引流到外部浏览器
   * bt是原 token
   */
  else if (to.query && to.query.bt) {
    const query = { ...to.query }
    if (isInThirdAppBrowser()) {
      await getUserInfo(to, next)
      void reportH5Ad(to)
      next()
      return
    }

    await userStore.transferLogin(to.query.bt as string)
    delete query.bt

    next({
      ...to,
      name: to.name ?? undefined,
      query,
      replace: true,
    })
  }
  // 首次登录
  else if (to.meta?.requiresAuth !== false && !userStore.isLogin()) {
    let deviceUuid = $getDeviceId()
    if (!deviceUuid) {
      deviceUuid = uuid()
      $setDeviceId(deviceUuid)
    }
    try {
      // 先收集归因信息, 再登录
      await useAttribution().init(to)
      await userStore.login(deviceUuid)
    }
    catch (error: any) {
      // login 失败/超时不能让导航静默中止(白屏无响应),跳错误页并带 redirect 供重试
      console.error(error)
      return next({
        name: 'error',
        query: {
          level: ERROR_PAGE_LEVEL.DEFAULT,
          message: error?.msg || error?.message,
          redirect: window.encodeURIComponent(window.location.href),
        },
        replace: true,
      })
    }
    await getUserInfo(to, next)
    void reportH5Ad(to)
    next()
  }
  // 已登录
  else {
    await getUserInfo(to, next)
    void reportH5Ad(to)
    next()
  }
})

// Clarity
router.afterEach((to) => {
  if (window.clarity) {
    window.clarity('event', to.name)
    window.clarity('set', 'route', to.name)
  }
})

// Axon page_view
router.afterEach(() => {
  const { reportPageView } = useAxonPageView()
  reportPageView()
})

// Store the route stack
router.afterEach((to, from) => {
  pageViewTrack(setPageViewParams(to.name!, to.path))

  const historyStore = useHistoryStore()

  if (to.name === 'history' && to.query.from === 'member')
    historyStore.$push(to.fullPath)

  // Note: same route || tabs route
  else if (to.name === from.name || ['history', 'home', 'member'].includes(to.name ?? ''))
    historyStore.$replace(to.fullPath)

  else
    historyStore.$push(to.fullPath)
})

export default router
